// @ts-ignore
import { hasProp, assert } from "@utils/assertion";
// @ts-ignore
import { isArray } from "@utils/arrayUtils";
// @ts-ignore
import { parseJsonWithNumbersAsBigint, renderJsonWithNumbersAsBigint, type ParserResult } from "@utils/json";
// @ts-ignore
import { toBase64 } from "@utils/blobUtils";
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
import type { MobileAppLoginPreferenceListView, MobileDevice, Language, PushFrequency, OAuth2Token, CustomerAdminMobileAppForm } from "../schema";

export function serialize_CustomerAdminMobileAppForm(obj: CustomerAdminMobileAppForm): string {
  const serialized = ((value: { loginId: number; loginPreferences: MobileAppLoginPreferenceListView[]; mobileDevices: MobileDevice[]; oAuth2Tokens: OAuth2Token[]; showLoginPreferences: boolean }): unknown => {
    return {
      loginId: ((value: number): unknown => {
        return value;
      })(value["loginId"]),
      loginPreferences: ((value: MobileAppLoginPreferenceListView[]): unknown => {
        return value.map((value: { corporateId: number; corporationName: string }): unknown => {
          return {
            corporateId: ((value: number): unknown => {
              return value;
            })(value["corporateId"]),
            corporationName: ((value: string): unknown => {
              return value;
            })(value["corporationName"]),
          };
        });
      })(value["loginPreferences"]),
      mobileDevices: ((value: MobileDevice[]): unknown => {
        return value.map(
          (value: {
            corporateId: number;
            customerId: number;
            deviceId: string;
            deviceType: string;
            firstUsedAt: DateTime<true>;
            language: Language;
            lastPushAt: DateTime<true> | null;
            lastUsedAt: DateTime<true>;
            osType: string | null;
            pushFrequency: PushFrequency;
            pushToken: string | null;
            version: string;
          }): unknown => {
            return {
              corporateId: ((value: number): unknown => {
                return value;
              })(value["corporateId"]),
              customerId: ((value: number): unknown => {
                return value;
              })(value["customerId"]),
              deviceId: ((value: string): unknown => {
                return value;
              })(value["deviceId"]),
              deviceType: ((value: string): unknown => {
                return value;
              })(value["deviceType"]),
              firstUsedAt: ((value: DateTime<true>): unknown => {
                return value.toMillis();
              })(value["firstUsedAt"]),
              language: ((value: "en" | "de" | "es" | "ru" | "zh" | "pt" | "fr"): unknown => {
                return value;
              })(value["language"]),
              lastPushAt: ((value: DateTime<true> | null): unknown => {
                if (value === null) {
                  return null;
                }
                return value.toMillis();
              })(value["lastPushAt"]),
              lastUsedAt: ((value: DateTime<true>): unknown => {
                return value.toMillis();
              })(value["lastUsedAt"]),
              osType: ((value: string | null): unknown => {
                if (value === null) {
                  return null;
                }
                return value;
              })(value["osType"]),
              pushFrequency: ((value: "NONE" | "DAILY" | "ONCE_A_WEEK" | "TWICE_A_MONTH" | "ONCE_A_MONTH"): unknown => {
                return value;
              })(value["pushFrequency"]),
              pushToken: ((value: string | null): unknown => {
                if (value === null) {
                  return null;
                }
                return value;
              })(value["pushToken"]),
              version: ((value: string): unknown => {
                return value;
              })(value["version"]),
            };
          },
        );
      })(value["mobileDevices"]),
      oAuth2Tokens: ((value: OAuth2Token[]): unknown => {
        return value.map(
          (value: {
            accessTokenExpiredAt: DateTime<true> | null;
            accessTokenValue: string | null;
            corporateId: number;
            isExpired: boolean;
            refreshTokenExpiredAt: DateTime<true> | null;
            refreshTokenValue: string | null;
            tokenType: string | null;
          }): unknown => {
            return {
              accessTokenExpiredAt: ((value: DateTime<true> | null): unknown => {
                if (value === null) {
                  return null;
                }
                return value.toMillis();
              })(value["accessTokenExpiredAt"]),
              accessTokenValue: ((value: string | null): unknown => {
                if (value === null) {
                  return null;
                }
                return value;
              })(value["accessTokenValue"]),
              corporateId: ((value: number): unknown => {
                return value;
              })(value["corporateId"]),
              isExpired: ((value: boolean): unknown => {
                return value;
              })(value["isExpired"]),
              refreshTokenExpiredAt: ((value: DateTime<true> | null): unknown => {
                if (value === null) {
                  return null;
                }
                return value.toMillis();
              })(value["refreshTokenExpiredAt"]),
              refreshTokenValue: ((value: string | null): unknown => {
                if (value === null) {
                  return null;
                }
                return value;
              })(value["refreshTokenValue"]),
              tokenType: ((value: string | null): unknown => {
                if (value === null) {
                  return null;
                }
                return value;
              })(value["tokenType"]),
            };
          },
        );
      })(value["oAuth2Tokens"]),
      showLoginPreferences: ((value: boolean): unknown => {
        return value;
      })(value["showLoginPreferences"]),
    };
  })(obj);
  return renderJsonWithNumbersAsBigint(serialized);
}
export function deserialize_CustomerAdminMobileAppForm(str: string): ParserResult<CustomerAdminMobileAppForm> {
  return ((
    value: unknown,
  ): ParserResult<{
    loginId: number;
    loginPreferences: MobileAppLoginPreferenceListView[];
    mobileDevices: MobileDevice[];
    oAuth2Tokens: OAuth2Token[];
    showLoginPreferences: boolean;
  }> => {
    if (typeof value !== "object") {
      return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm]: '${value}' expected to be of type 'object'.` };
    }
    if (value === null) {
      return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm]: '${value}' expected to not be null.` };
    }
    if (!hasProp(value, "loginId")) {
      return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm/loginId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'loginId'.` };
    }
    const parsed_loginId = ((): ParserResult<number> => {
      return ((value: unknown): ParserResult<number> => {
        if (typeof value === "number") {
          return { success: true, value: value };
        }
        return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm/loginId]: '${value}' expected to be of type number.` };
      })(value["loginId"]);
    })();
    if (!parsed_loginId.success) {
      return { success: false, reason: parsed_loginId.reason };
    }
    if (!hasProp(value, "loginPreferences")) {
      return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm/loginPreferences]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'loginPreferences'.` };
    }
    const parsed_loginPreferences = ((): ParserResult<MobileAppLoginPreferenceListView[]> => {
      return ((value: unknown): ParserResult<MobileAppLoginPreferenceListView[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm/loginPreferences]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<MobileAppLoginPreferenceListView> => {
          return ((
            value: unknown,
          ): ParserResult<{
            corporateId: number;
            corporationName: string;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/MobileAppLoginPreferenceListView]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/MobileAppLoginPreferenceListView]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "corporateId")) {
              return { success: false, reason: `[#/components/schemas/MobileAppLoginPreferenceListView/corporateId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'corporateId'.` };
            }
            const parsed_corporateId = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/MobileAppLoginPreferenceListView/corporateId]: '${value}' expected to be of type number.` };
              })(value["corporateId"]);
            })();
            if (!parsed_corporateId.success) {
              return { success: false, reason: parsed_corporateId.reason };
            }
            if (!hasProp(value, "corporationName")) {
              return { success: false, reason: `[#/components/schemas/MobileAppLoginPreferenceListView/corporationName]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'corporationName'.` };
            }
            const parsed_corporationName = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/MobileAppLoginPreferenceListView/corporationName]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["corporationName"]);
            })();
            if (!parsed_corporationName.success) {
              return { success: false, reason: parsed_corporationName.reason };
            }
            return { success: true, value: { corporateId: parsed_corporateId.value, corporationName: parsed_corporationName.value } };
          })(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/CustomerAdminMobileAppForm/loginPreferences]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["loginPreferences"]);
    })();
    if (!parsed_loginPreferences.success) {
      return { success: false, reason: parsed_loginPreferences.reason };
    }
    if (!hasProp(value, "mobileDevices")) {
      return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm/mobileDevices]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'mobileDevices'.` };
    }
    const parsed_mobileDevices = ((): ParserResult<MobileDevice[]> => {
      return ((value: unknown): ParserResult<MobileDevice[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm/mobileDevices]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<MobileDevice> => {
          return ((
            value: unknown,
          ): ParserResult<{
            corporateId: number;
            customerId: number;
            deviceId: string;
            deviceType: string;
            firstUsedAt: DateTime<true>;
            language: Language;
            lastPushAt: DateTime<true> | null;
            lastUsedAt: DateTime<true>;
            osType: string | null;
            pushFrequency: PushFrequency;
            pushToken: string | null;
            version: string;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/MobileDevice]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/MobileDevice]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "corporateId")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/corporateId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'corporateId'.` };
            }
            const parsed_corporateId = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/MobileDevice/corporateId]: '${value}' expected to be of type number.` };
              })(value["corporateId"]);
            })();
            if (!parsed_corporateId.success) {
              return { success: false, reason: parsed_corporateId.reason };
            }
            if (!hasProp(value, "customerId")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/customerId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'customerId'.` };
            }
            const parsed_customerId = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/MobileDevice/customerId]: '${value}' expected to be of type number.` };
              })(value["customerId"]);
            })();
            if (!parsed_customerId.success) {
              return { success: false, reason: parsed_customerId.reason };
            }
            if (!hasProp(value, "deviceId")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/deviceId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'deviceId'.` };
            }
            const parsed_deviceId = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/deviceId]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["deviceId"]);
            })();
            if (!parsed_deviceId.success) {
              return { success: false, reason: parsed_deviceId.reason };
            }
            if (!hasProp(value, "deviceType")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/deviceType]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'deviceType'.` };
            }
            const parsed_deviceType = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/deviceType]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["deviceType"]);
            })();
            if (!parsed_deviceType.success) {
              return { success: false, reason: parsed_deviceType.reason };
            }
            if (!hasProp(value, "firstUsedAt")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/firstUsedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'firstUsedAt'.` };
            }
            const parsed_firstUsedAt = ((): ParserResult<DateTime<true>> => {
              return ((value: unknown): ParserResult<DateTime<true>> => {
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/firstUsedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/firstUsedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["firstUsedAt"]);
            })();
            if (!parsed_firstUsedAt.success) {
              return { success: false, reason: parsed_firstUsedAt.reason };
            }
            if (!hasProp(value, "language")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/language]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'language'.` };
            }
            const parsed_language = ((): ParserResult<Language> => {
              return ((value: unknown): ParserResult<Language> => {
                return ((value: unknown): ParserResult<"en" | "de" | "es" | "ru" | "zh" | "pt" | "fr"> => {
                  if (typeof value !== "string") {
                    return { success: false, reason: `[#/components/schemas/Language]: '${value}' expected to be of type string.` };
                  }
                  switch (value) {
                    case "en":
                      return { success: true, value: "en" };
                    case "de":
                      return { success: true, value: "de" };
                    case "es":
                      return { success: true, value: "es" };
                    case "ru":
                      return { success: true, value: "ru" };
                    case "zh":
                      return { success: true, value: "zh" };
                    case "pt":
                      return { success: true, value: "pt" };
                    case "fr":
                      return { success: true, value: "fr" };
                    default:
                      return { success: false, reason: `[#/components/schemas/Language]: '${value}' expected to be enum, one of 'en, de, es, ru, zh, pt, fr'.` };
                  }
                })(value);
              })(value["language"]);
            })();
            if (!parsed_language.success) {
              return { success: false, reason: parsed_language.reason };
            }
            if (!hasProp(value, "lastPushAt")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/lastPushAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'lastPushAt'.` };
            }
            const parsed_lastPushAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/lastPushAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/lastPushAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["lastPushAt"]);
            })();
            if (!parsed_lastPushAt.success) {
              return { success: false, reason: parsed_lastPushAt.reason };
            }
            if (!hasProp(value, "lastUsedAt")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/lastUsedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'lastUsedAt'.` };
            }
            const parsed_lastUsedAt = ((): ParserResult<DateTime<true>> => {
              return ((value: unknown): ParserResult<DateTime<true>> => {
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/lastUsedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/lastUsedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["lastUsedAt"]);
            })();
            if (!parsed_lastUsedAt.success) {
              return { success: false, reason: parsed_lastUsedAt.reason };
            }
            if (!hasProp(value, "osType")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/osType]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'osType'.` };
            }
            const parsed_osType = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/osType]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["osType"]);
            })();
            if (!parsed_osType.success) {
              return { success: false, reason: parsed_osType.reason };
            }
            if (!hasProp(value, "pushFrequency")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/pushFrequency]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'pushFrequency'.` };
            }
            const parsed_pushFrequency = ((): ParserResult<PushFrequency> => {
              return ((value: unknown): ParserResult<PushFrequency> => {
                return ((value: unknown): ParserResult<"NONE" | "DAILY" | "ONCE_A_WEEK" | "TWICE_A_MONTH" | "ONCE_A_MONTH"> => {
                  if (typeof value !== "string") {
                    return { success: false, reason: `[#/components/schemas/PushFrequency]: '${value}' expected to be of type string.` };
                  }
                  switch (value) {
                    case "NONE":
                      return { success: true, value: "NONE" };
                    case "DAILY":
                      return { success: true, value: "DAILY" };
                    case "ONCE_A_WEEK":
                      return { success: true, value: "ONCE_A_WEEK" };
                    case "TWICE_A_MONTH":
                      return { success: true, value: "TWICE_A_MONTH" };
                    case "ONCE_A_MONTH":
                      return { success: true, value: "ONCE_A_MONTH" };
                    default:
                      return { success: false, reason: `[#/components/schemas/PushFrequency]: '${value}' expected to be enum, one of 'NONE, DAILY, ONCE_A_WEEK, TWICE_A_MONTH, ONCE_A_MONTH'.` };
                  }
                })(value);
              })(value["pushFrequency"]);
            })();
            if (!parsed_pushFrequency.success) {
              return { success: false, reason: parsed_pushFrequency.reason };
            }
            if (!hasProp(value, "pushToken")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/pushToken]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'pushToken'.` };
            }
            const parsed_pushToken = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/pushToken]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["pushToken"]);
            })();
            if (!parsed_pushToken.success) {
              return { success: false, reason: parsed_pushToken.reason };
            }
            if (!hasProp(value, "version")) {
              return { success: false, reason: `[#/components/schemas/MobileDevice/version]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'version'.` };
            }
            const parsed_version = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/MobileDevice/version]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["version"]);
            })();
            if (!parsed_version.success) {
              return { success: false, reason: parsed_version.reason };
            }
            return {
              success: true,
              value: {
                corporateId: parsed_corporateId.value,
                customerId: parsed_customerId.value,
                deviceId: parsed_deviceId.value,
                deviceType: parsed_deviceType.value,
                firstUsedAt: parsed_firstUsedAt.value,
                language: parsed_language.value,
                lastPushAt: parsed_lastPushAt.value,
                lastUsedAt: parsed_lastUsedAt.value,
                osType: parsed_osType.value,
                pushFrequency: parsed_pushFrequency.value,
                pushToken: parsed_pushToken.value,
                version: parsed_version.value,
              },
            };
          })(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/CustomerAdminMobileAppForm/mobileDevices]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["mobileDevices"]);
    })();
    if (!parsed_mobileDevices.success) {
      return { success: false, reason: parsed_mobileDevices.reason };
    }
    if (!hasProp(value, "oAuth2Tokens")) {
      return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm/oAuth2Tokens]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'oAuth2Tokens'.` };
    }
    const parsed_oAuth2Tokens = ((): ParserResult<OAuth2Token[]> => {
      return ((value: unknown): ParserResult<OAuth2Token[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm/oAuth2Tokens]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<OAuth2Token> => {
          return ((
            value: unknown,
          ): ParserResult<{
            accessTokenExpiredAt: DateTime<true> | null;
            accessTokenValue: string | null;
            corporateId: number;
            isExpired: boolean;
            refreshTokenExpiredAt: DateTime<true> | null;
            refreshTokenValue: string | null;
            tokenType: string | null;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/OAuth2Token]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/OAuth2Token]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "accessTokenExpiredAt")) {
              return { success: false, reason: `[#/components/schemas/OAuth2Token/accessTokenExpiredAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'accessTokenExpiredAt'.` };
            }
            const parsed_accessTokenExpiredAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/OAuth2Token/accessTokenExpiredAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/OAuth2Token/accessTokenExpiredAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["accessTokenExpiredAt"]);
            })();
            if (!parsed_accessTokenExpiredAt.success) {
              return { success: false, reason: parsed_accessTokenExpiredAt.reason };
            }
            if (!hasProp(value, "accessTokenValue")) {
              return { success: false, reason: `[#/components/schemas/OAuth2Token/accessTokenValue]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'accessTokenValue'.` };
            }
            const parsed_accessTokenValue = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/OAuth2Token/accessTokenValue]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["accessTokenValue"]);
            })();
            if (!parsed_accessTokenValue.success) {
              return { success: false, reason: parsed_accessTokenValue.reason };
            }
            if (!hasProp(value, "corporateId")) {
              return { success: false, reason: `[#/components/schemas/OAuth2Token/corporateId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'corporateId'.` };
            }
            const parsed_corporateId = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/OAuth2Token/corporateId]: '${value}' expected to be of type number.` };
              })(value["corporateId"]);
            })();
            if (!parsed_corporateId.success) {
              return { success: false, reason: parsed_corporateId.reason };
            }
            if (!hasProp(value, "isExpired")) {
              return { success: false, reason: `[#/components/schemas/OAuth2Token/isExpired]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'isExpired'.` };
            }
            const parsed_isExpired = ((): ParserResult<boolean> => {
              return ((value: unknown): ParserResult<boolean> => {
                if (typeof value !== "boolean") {
                  return { success: false, reason: `[#/components/schemas/OAuth2Token/isExpired]: '${value}' expected to be of type boolean.` };
                }
                return { success: true, value: value };
              })(value["isExpired"]);
            })();
            if (!parsed_isExpired.success) {
              return { success: false, reason: parsed_isExpired.reason };
            }
            if (!hasProp(value, "refreshTokenExpiredAt")) {
              return { success: false, reason: `[#/components/schemas/OAuth2Token/refreshTokenExpiredAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'refreshTokenExpiredAt'.` };
            }
            const parsed_refreshTokenExpiredAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/OAuth2Token/refreshTokenExpiredAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/OAuth2Token/refreshTokenExpiredAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["refreshTokenExpiredAt"]);
            })();
            if (!parsed_refreshTokenExpiredAt.success) {
              return { success: false, reason: parsed_refreshTokenExpiredAt.reason };
            }
            if (!hasProp(value, "refreshTokenValue")) {
              return { success: false, reason: `[#/components/schemas/OAuth2Token/refreshTokenValue]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'refreshTokenValue'.` };
            }
            const parsed_refreshTokenValue = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/OAuth2Token/refreshTokenValue]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["refreshTokenValue"]);
            })();
            if (!parsed_refreshTokenValue.success) {
              return { success: false, reason: parsed_refreshTokenValue.reason };
            }
            if (!hasProp(value, "tokenType")) {
              return { success: false, reason: `[#/components/schemas/OAuth2Token/tokenType]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'tokenType'.` };
            }
            const parsed_tokenType = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/OAuth2Token/tokenType]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["tokenType"]);
            })();
            if (!parsed_tokenType.success) {
              return { success: false, reason: parsed_tokenType.reason };
            }
            return {
              success: true,
              value: {
                accessTokenExpiredAt: parsed_accessTokenExpiredAt.value,
                accessTokenValue: parsed_accessTokenValue.value,
                corporateId: parsed_corporateId.value,
                isExpired: parsed_isExpired.value,
                refreshTokenExpiredAt: parsed_refreshTokenExpiredAt.value,
                refreshTokenValue: parsed_refreshTokenValue.value,
                tokenType: parsed_tokenType.value,
              },
            };
          })(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/CustomerAdminMobileAppForm/oAuth2Tokens]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["oAuth2Tokens"]);
    })();
    if (!parsed_oAuth2Tokens.success) {
      return { success: false, reason: parsed_oAuth2Tokens.reason };
    }
    if (!hasProp(value, "showLoginPreferences")) {
      return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm/showLoginPreferences]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'showLoginPreferences'.` };
    }
    const parsed_showLoginPreferences = ((): ParserResult<boolean> => {
      return ((value: unknown): ParserResult<boolean> => {
        if (typeof value !== "boolean") {
          return { success: false, reason: `[#/components/schemas/CustomerAdminMobileAppForm/showLoginPreferences]: '${value}' expected to be of type boolean.` };
        }
        return { success: true, value: value };
      })(value["showLoginPreferences"]);
    })();
    if (!parsed_showLoginPreferences.success) {
      return { success: false, reason: parsed_showLoginPreferences.reason };
    }
    return {
      success: true,
      value: { loginId: parsed_loginId.value, loginPreferences: parsed_loginPreferences.value, mobileDevices: parsed_mobileDevices.value, oAuth2Tokens: parsed_oAuth2Tokens.value, showLoginPreferences: parsed_showLoginPreferences.value },
    };
  })(parseJsonWithNumbersAsBigint(str));
}
